/**
 * Rewriting a passage, at the passage.
 *
 * It used to be a card two screens below the editor. That is a long way from
 * the paragraph it acts on, and the distance was the feature's problem rather
 * than an inconvenience: the writer selected a sentence, scrolled down past
 * 「AI生成」 to find the panel, and by the time they were reading the result they
 * could no longer see the text it was replacing. A card also has to SAY what it
 * is attached to - 「選択中：42文字」 above a grey quotation of the passage - which
 * is a paragraph of chrome describing something that was on screen a moment ago.
 *
 * Anchored to the selection instead, so the thing being rewritten and the
 * controls that rewrite it are in one glance, and nothing has to be quoted back.
 *
 * WHY THE SELECTION SURVIVES OPENING IT. Focus moves into the popover's input,
 * which blurs the editor - but ProseMirror keeps its selection through a blur,
 * and the passage is captured in the parent's state the moment it is made. So
 * the range this acts on is the range that was highlighted, not whatever the
 * browser thinks is selected once a textbox has focus.
 *
 * Portalled to the popup host for the reason every popup in this app is: each
 * card sets `overflow: hidden` to clip its corner flower, and a panel rendered
 * inside one is cut off at the card's edge. See popup-host.ts.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { popupHost } from '../popup-host.ts';
import type { RewriteResult } from '../api.ts';

/**
 * The rewrite operations, in the writer's words.
 *
 * The values are the server's vocabulary (REWRITE_STYLES in ai/prompt.ts); the
 * labels are what a person picks. Free text would give three spellings of "make
 * it shorter", each turning into a different instruction.
 */
export const REWRITE_STYLES = [
  { value: 'shorter', label: '短くする' },
  { value: 'longer', label: '詳しくする' },
  { value: 'simpler', label: 'やさしく' },
  { value: 'professional', label: '専門的に' },
  { value: 'bullets', label: '箇条書きに' },
  { value: 'softer', label: 'やわらかく' },
];

/** How far below the selection the panel sits, and its width. */
const GAP = 8;
const WIDTH = 380;

interface Position {
  left: number;
  top: number;
}

export function RewritePopover({
  selected,
  anchor,
  style,
  onStyle,
  note,
  onNote,
  busy,
  error,
  result,
  onRun,
  onApply,
  onDiscard,
  onClose,
}: {
  /** The passage, as the editor reported it. '' means nothing is selected. */
  selected: string;
  /** Where that passage is, in PAGE coordinates. See RichEditor. */
  anchor: DOMRect | null;
  style: string;
  onStyle: (value: string) => void;
  note: string;
  onNote: (value: string) => void;
  busy: boolean;
  /**
   * Why the last attempt produced nothing.
   *
   * Shown HERE rather than only in the page's alert strip. A rewrite started
   * from a finding scrolls the writer to the passage and opens this panel over
   * it - so a refusal (「リライトにはペルソナが必要です」) was being written into a
   * banner two screens above, and from where they were standing the button
   * simply did nothing.
   */
  error?: string | null;
  result: RewriteResult | null;
  onRun: () => void;
  onApply: () => void;
  onDiscard: () => void;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  const open = selected.trim() !== '' && anchor !== null;

  useLayoutEffect(() => {
    if (!open || !anchor) {
      setPosition(null);
      return;
    }
    /*
     * Below the passage where there is room on screen, above it where there is
     * not - a sentence near the foot of the window would otherwise put the
     * controls out of view, which is the one place they must never be. The
     * comparison is in viewport space (is there room BELOW right now); the
     * answer is in page space, which is what the anchor is in.
     */
    const below = window.innerHeight - (anchor.bottom - window.scrollY) > 260;
    const height = panelRef.current?.offsetHeight ?? 220;
    setPosition({
      left: Math.min(
        Math.max(window.scrollX + 8, anchor.left),
        window.scrollX + Math.max(8, window.innerWidth - WIDTH - 8),
      ),
      top: below ? anchor.bottom + GAP : Math.max(window.scrollY + 8, anchor.top - height - GAP),
    });
  }, [open, anchor, result]);

  // Escape closes it, the way it closes every other popup here.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      className="rewritepop"
      role="dialog"
      aria-label="選択範囲を書き直す"
      style={{
        left: position?.left ?? -9999,
        top: position?.top ?? -9999,
        width: WIDTH,
        // Measured on the first render, placed on the second: showing it at the
        // fallback coordinates for one frame is a panel that jumps.
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      <div className="rewritepop__head">
        <span className="rewritepop__title">選択範囲を書き直す</span>
        <span className="muted small">{selected.length}文字</span>
        <button
          type="button"
          className="rewritepop__close"
          aria-label="閉じる"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      {error && (
        <p className="alert small" role="alert" style={{ margin: 0 }}>
          {error}
        </p>
      )}

      {/*
        WHAT IS BEING CHANGED, while it is being changed.
        
        A fix started from a finding scrolls the writer to a sentence and then
        covered it with a busy panel: they were told something was being
        rewritten without being shown what. The original stays on screen for the
        whole operation - struck through once a replacement exists, so the change
        itself is the thing being read rather than two paragraphs to compare by
        eye.
      */}
      {(busy || result) && (
        <div className={`rewritepop__before${result ? ' rewritepop__before--done' : ''}`}>
          {selected}
        </div>
      )}
      {busy && !result && (
        <p className="rewritepop__working" role="status">
          書き直しています…
        </p>
      )}

      {result ? (
        <>
          <div className="rewritepop__result">{result.text}</div>
          {/*
            The server re-checks every replacement against the expression rules
            (and retries a fix once). What is still tripped is said HERE, before
            置き換える - not discovered on the next check run.
          */}
          {result.stillFlagged && result.stillFlagged.length > 0 && (
            <p className="alert alert--warn small" role="status" style={{ margin: 0 }}>
              この案にも「{result.stillFlagged.join('」「')}」に当たる表現が残っています。
              置き換える前に内容を確認するか、指示を書いて書き直してください。
            </p>
          )}
          <div className="rewritepop__actions">
            <button className="btn btn--sm" type="button" onClick={onApply}>
              置き換える
            </button>
            <button className="btn btn--ghost btn--sm" type="button" onClick={onDiscard}>
              やり直す
            </button>
            <span className="muted small">保存で確定（{result.provider}）</span>
          </div>
        </>
      ) : busy ? null : (
        <>
          <div className="chips">
            {REWRITE_STYLES.map((option) => (
              <button
                key={option.value}
                type="button"
                className="chip"
                aria-pressed={style === option.value}
                onClick={() => onStyle(style === option.value ? '' : option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          {/*
            The instruction, in the panel rather than in a card of its own. A
            style chip covers 「短くする」; this is for 「ダウンタイムの説明を具体
            的に」, which is the request people actually have.
          */}
          <input
            className="rewritepop__note"
            maxLength={500}
            placeholder="指示（任意）：例 ダウンタイムの説明を具体的に"
            value={note}
            onChange={(event) => onNote(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !busy) {
                event.preventDefault();
                onRun();
              }
            }}
          />
          <div className="rewritepop__actions">
            <button className="btn btn--sm" type="button" disabled={busy} onClick={onRun}>
              {busy ? '書き直し中…' : '書き直す'}
            </button>
          </div>
        </>
      )}
    </div>,
    popupHost(panelRef.current ?? document.body),
  );
}
